import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import React from "react";
import WithLove from "../../common/components/WithLove";
import Topic from "../../common/components/Topic";
import Picture from "../../common/components/Picture";
import Title from "../../common/components/Title";
import Content from "../../common/components/Content";
import Subtitle from "../../common/components/Subtitle";
import Text from "../../common/components/Text";
import SubSection from "../../common/components/SubSection";
import Section from "../../common/components/Section";
// import Number from "../../common/components/Number";
import Bullet from "../../common/components/Bullet";
import Italics from "../../common/components/Italics";

// Images Import
// import cover from "../../images/6.0/day1/Cover Image.jpg";
// import kingdomTable from "../../images/6.0/day1/Kingdom Table.jpg";

import cover from "../../images/5.0/day1/0. Introduction.png";
import what from "../../images/5.0/day1/1. What.png";
import why from "../../images/5.0/day1/2. Why.jpg";
import more from "../../images/5.0/day1/3. More.png";
import exegesis from "../../images/5.0/day1/4. Exegesis.png";
import knowledge from "../../images/5.0/day1/5. Knowledge.png";
import milk from "../../images/5.0/day1/6. Milk.png";
import faith from "../../images/5.0/day1/7. Faith.jpg";
import transitioning from "../../images/5.0/day1/8. Transitioning.png";
import minister from "../../images/5.0/day1/9. Minister.jpg";

export default function DayOne() {
  return (
    <>
      <Box>
        <Topic>The Fundamentals of the Will</Topic>
        <Text>Romans 12:1-2, Colossians 1:9-11, Matthew 7:21</Text>
        <br />
      </Box>

      {/* Cover Image */}
      <Picture src={cover} />

      <Section>
        {/* Introduction */}
        <Section>
          <Title>Introduction</Title>
          <Content>
            Welcome to Gadites Bible Seminar 2026, themed{" "}
            <b>"The Volume of His Beautiful Will"</b>. Last year, we considered
            the theme <b>"Thy Brethren The Prophets"</b>, and we were immensely
            enlightened and transformed to see our predestination and
            glorification as believers and the process of arriving there.
            However, we cannot embark on that journey without the will.
            <br />
            <br />
            It is interesting to observe how the Lord is carefully guiding us in
            understanding, feeding us as his tender flock, and we are grateful
            to our Great and Good Shepherd, Jesus Christ. We trust that this
            year we will all be baptized in Word and Spirit to become doers of
            his will in Jesus name, Amen!
          </Content>
        </Section>

        {/* Section 1: What is the Will of God? */}
        <Section>
          <Title>1. What is the Will of God?</Title> <br />
          <Picture src={what} />
          <br />
          <br />
          <Content>
            The phrase "will of God" is commonly used among believers and can
            describe different realities depending on the context of use. It is
            important that we lay out these definitions to avoid any confusion
            regarding the context of use in this study. We can define the will
            of God in the following ways:
          </Content>
          <SubSection>
            <Content>
              <Subtitle>a) God's Intention or Desire</Subtitle>
              The will of God can be seen as God's ultimate intention for
              creation or His ultimate plan for man <b>(Eph. 1:5; Eph. 1:11)</b>
              . This intention is simply that man would be in his exact image
              and likeness <b>(Rom 8:22-25)</b>. Another example, according to{" "}
              <b>1 Timothy 2:4</b>, is God's intention for all men to be saved
              and come to the knowledge of the truth. We also see in{" "}
              <b>1 Thess. 4:3</b> that his will for us is our sanctification.
            </Content>
          </SubSection>
          <SubSection>
            <Content>
              <Subtitle>b) God's Choice or Preference</Subtitle>
              The will of God can also describe the objective choice or
              preference of God concerning a matter{" "}
              <b>(Luke 22:42, Rom. 9:15)</b>. For example, when God chose David
              over Saul <b>(Isaiah 14:1)</b> and preferred David over all his
              brothers <b>(2 Sam. 16:12)</b>. Another example is God's choice of
              Matthias over Barsabas <b>(Acts 1:23-26)</b>. Paul also uses it in
              this sense <b>(Rom. 1:10)</b>.
            </Content>
          </SubSection>
          <SubSection>
            <Content>
              <Subtitle>c) God's Laws or Constitution</Subtitle>
              One profound definition that stands out in Scripture is the
              definition of the will of God — not merely as His preference, but
              as the body of laws or principles that govern or guide His life.
              This definition is from <b>Psalm 40:7-8</b>, which is one of the
              clearest descriptions of the will of God.
            </Content>
            <Italics>
              Psalm 40:7-8: Then said I, Lo, I come: in the volume of the book
              it is written of me, I DELIGHT TO DO THY WILL, O MY GOD: YEA, THY
              LAW IS WITHIN MY HEART.
            </Italics>
            <Content>
              Interestingly, the laws of a thing are the hidden principles that
              are responsible for the observable behaviour or expression of that
              thing. We may thus describe the laws of a thing as the secrets of
              that thing. God designed all creation to operate by laws because
              God himself lives by certain laws <b>(Heb 8:10, 1 Sam 2:35)</b>.
              Thus, we may describe the will of God as the laws that generate
              his life or kingdom. The will of God is the secret of his life.
            </Content>
          </SubSection>
          <SubSection>
            <Content>
              <Subtitle>Our Definition</Subtitle>
              The will of God, therefore, is the body of laws, principles, or
              standards by which God lives <b>(Psalm 40:7–8)</b>. In other
              words, God's nature and judgment are informed by the laws that are
              inherent in Him, and the sum of these laws is called the will of
              God <b>(Acts 13:22b)</b>. By God's grace, this is the definition
              we would be adopting in this study.
            </Content>
          </SubSection>
        </Section>

        {/* Section 2: Why the Will of God? */}
        <Section>
          <Title>2. Why the Will of God?</Title>
          <br />
          <Picture src={why} />
          <br />
          <br />
          <Content>
            It is important that we understand the essence or purpose of the
            will of God because when the purpose of a thing is not known, abuse
            is inevitable. Therefore, understanding the purpose of the will
            inspires us to seek and obey it. We may comprehend the purpose of
            the will from two sides: to generate pleasure for God and to access
            the dominion of God.
          </Content>
          <SubSection>
            <Content>
              <Subtitle>a) Pleasure</Subtitle>
              God is not a thing, or a concept, but a person. This means that he
              has a personality. <b>Hebrews 1:1-4</b> shows us that the Son, our
              Lord Jesus Christ, is the express image of his person.
              Consequently, he can be pleased <b>(Matt 3:17)</b> or grieved{" "}
              <b>(Gen 6:6)</b>. Interestingly, we find that the secret to
              pleasing God is doing his will <b>(Col 1:9-10)</b>. Thus, the will
              of God is what pleases God.
              <br />
              <br />
              The Hebrew word for "will" is <b>"ratson"</b>, which means
              "delight or pleasure". Also, the Greek word for "will" is{" "}
              <b>"Thelema"</b>, which means "delight". Moreover, the Yoruba
              interpretation of this phrase gives us an interesting insight. The
              Yoruba translation for the phrase "will of God" is{" "}
              <b>"ife-ti-baba"</b>. The transliteration of this word in English
              is "that which God loves or delights in." Hence, we may conclude
              that the will of God is the secret to pleasing him.
            </Content>
          </SubSection>
          <SubSection>
            <Content>
              <Subtitle>b) Dominion</Subtitle>
              We find that "will" is almost always used in the same context with
              "kingdom" in the scriptures <b>(Matt 7:21, 6:10)</b>. We can see
              that there is a relationship between the will of God and the
              kingdom of God. The relationship is such that the will is the key
              to accessing the kingdom. We must renew our minds concerning the
              meaning of the word "kingdom". It is significant to know that the
              kingdom of God is not a location <b>(Luke 17:21, Psalm 103:19)</b>
              , nor the attainment of any earthly status <b>(John 18:36)</b>,
              but rather the kingdom of God is the expression of God's life{" "}
              <b>(Romans 14:17; Matthew 18:3,8)</b>.
              <br />
              <br />
              The Yoruba interpretation of the word "kingdom" is <b>"ijoba"</b>,
              which means "government". A government is not a place but the
              expression of reign or rule. Thus, God's kingdom is his
              government, which flows from within him. Every government has a
              constitution, which is the body of laws that drives it. This
              constitution may be understood as the will that drives that
              dominion. Thus, doing the will grants you access to the dominion.
            </Content>
          </SubSection>
        </Section>

        {/* Section 3: More Biblical Insight */}
        <Section>
          <Title>3. More Biblical Insight into the Will</Title>
          <br />
          <Picture src={more} />
          <br />
          <br />
          <Italics>
            Psa 40:6-8 — Sacrifice and offering thou didst not desire; mine ears
            hast thou opened: burnt offering and sin offering hast thou not
            required. Then said I, Lo, I come: in the volume of the book it is
            written of me, I delight to do thy will, O my God: yea, thy law is
            within my heart.
          </Italics>
          <Content>
            We can find a deeper understanding of the will as described in the
            scriptures when we consider its contextual usage in the Old
            Testament and New Testament scriptures. According to{" "}
            <b>Hebrews 10:1, 5-9</b>, which references <b>Psalm 40:6-8</b>, we
            find that the shadow of the will of God are sacrifices and
            offerings. The old covenant, which is described as a type and shadow
            of the new, was characterized by sacrifices and offerings. These
            sacrifices and offerings are symbolic of the will of God{" "}
            <b>(Hosea 6:6, Psalm 51:16-17, Micah 6:6-8, 1 Sam 15:22)</b>. Thus,
            we may define{" "}
            <b>
              "the will of God as the law of spiritual sacrifices that are well
              pleasing to God"
            </b>
            .
            <br />
            <br />
            It is worth noting that not just anyone could offer sacrifices to
            God <b>(Heb 5:1)</b>. Only the priests were allowed to offer
            sacrifices to God <b>(Heb 8:3)</b>. This is because to offer these
            sacrifices, the priest must have both the knowledge and laws that
            govern each sacrifice and offering <b>(Hosea 4:6, Malachi 2:7)</b>.
            These patterns are from evil spirits that fell from heaven{" "}
            <b>(Heb 8:3-5)</b>.
          </Content>
          <Content>
            The Levitical priests were men who were schooled in the knowledge
            and law of the sacrifices and offerings that are well pleasing to
            God <b>(Leviticus 1:7-9)</b>. Interestingly, the image of these
            sacrifices is revealed under the new covenant. Not just any
            Christian can offer sacrifices well pleasing to God. It is only a
            Christian who is filled with the knowledge and law of the Lord (will
            of the Lord) that can generate spiritual sacrifices and offerings,
            which are well pleasing to the Lord <b>(Col 1:9-10, 1 Pet 2:5)</b>.
            It is good to mention that these Christians are not pastors but New
            Testament priests of the Lord. Let's also mention that these
            sacrifices are such that the Lord cannot refuse the one who is
            offering them because he is well pleased with such a person{" "}
            <b>(1 John 3:22)</b>.
            <br />
            <br />
            In sum, the will of God are the laws of his sacrifices and
            offerings, which, when done, binds God to the doer. A good example
            of this was Elijah, who, after offering to God on the altar,
            commanded fire from heaven <b>(1 Kings 18:30-39)</b>. The perfect
            example of this is Jesus, to whom God was committed all through his
            life on earth <b>(Matt 3:17, John 3:29, John 11:41-42)</b>.
          </Content>
          <Content>
            In sum, when the will of God is done, God is pleased and grants the
            doer access into his dominion. Indeed, God rewards every act of
            obedience <b>(Heb 11:6)</b>. However, when the will of God, which is
            His own laws, is revealed to a believer and obeyed, this pleases God
            to give His kingdom (His life) to him. Thus, the will of God is
            really what pleases God to commend His kingdom to man{" "}
            <b>(Colossians 1:9-10, Hebrews 10:8-9)</b>.
          </Content>
        </Section>

        {/* Section 4: Scriptural Exegesis */}
        <Section>
          <Title>
            4. Some Scriptural Exegesis Concerning the Kingdom and the Will
          </Title>
          <br />
          <Picture src={exegesis} />
          <br />
          <br />
          <SubSection>
            <Content>
              <Subtitle>
                A. The Kingdom is in the Holy Ghost (Rom 14:17)
              </Subtitle>
              The kingdom of God predates creation. The Kingdom is as much in
              the Father, and in the Word, as it is in the Holy Ghost. The
              Kingdom is what makes them one. These three are the beings of the
              Kingdom as they have all the fullness of the kingdom in
              themselves. The Word was made flesh, and as a man, he gradually
              and progressively received all of the kingdom in himself by doing
              and finishing the will of the Kingdom{" "}
              <b>(Matt 7:21, John 4:34, Heb 12:2)</b>. Therefore, Jesus embodies
              the kingdom of God as a man, making it possible for men to access
              the kingdom of God.
            </Content>
          </SubSection>
          <SubSection>
            <Content>
              <Subtitle>B. The Kingdom of Christ and of God (Eph 5:5)</Subtitle>
              The kingdom can also be understood in the context of statures or
              natures. In this context, the kingdom is described in terms of two
              statures: the stature of Christ and the stature of God. It is
              worth noting that "Christ" and "God" here refer to statures in the
              kingdom. These two natures dwell in Our Lord Jesus, which is why
              he is described as the Christ and the Son of God{" "}
              <b>(Matt 16:16)</b>. For example, a professor has at least three
              degrees of stature in himself: a graduate, a master's, and a
              doctorate. These are three statures in a single Professor. It is
              also the desire of God that the saints are also filled with the
              statures of the kingdom <b>(Eph 3:15-17)</b>.
            </Content>
          </SubSection>
          <SubSection>
            <Content>
              <Subtitle>
                C. The Kingdom is Righteousness, Peace, and Joy (Rom 14:17)
              </Subtitle>
              The Kingdom of God may also be understood in the context of life
              expressions <b>(Romans 14:17)</b>. In this context, the kingdom
              consists of three life expressions: righteousness, peace, and joy,
              in a progressive manner. All of these life expressions are in Our
              Lord Jesus Christ <b>(1 John 2:1, John 14:27, Heb 12:2)</b>.
            </Content>
            <Content>
              For example, Christ is described as "spiritual" in{" "}
              <b>1 Corinthians 10:4-5</b>, which, according to <b>Romans 8:6</b>
              , is a nature of "life and peace" (with "life" synonymous with
              "righteousness" as in <b>Romans 8:10</b>). Therefore, we can say
              that Christ is the expression of the "righteousness and peace" of
              the kingdom. Meanwhile, God is the expression of the "joy" in the
              kingdom, as can be seen in <b>Psalm 16:11, 43:4</b>. Thus, we may
              easily fit the three life expressions into the two statures as{" "}
              <b>Christ</b> (righteousness and peace) and <b>God</b> (Joy).
            </Content>
          </SubSection>
          <SubSection>
            <Content>
              <Subtitle>D. The Divisions of the Will</Subtitle>
            </Content>
            <Italics>
              Col 1:9-11 — For this cause we also, since the day we heard it, do
              not cease to pray for you, and to desire that ye might be filled
              with the knowledge of his will in all wisdom and spiritual
              understanding; That ye might walk worthy of the Lord unto all
              pleasing, being fruitful in every good work, and increasing in the
              knowledge of God…
            </Italics>
            <Content>
              As mentioned earlier, the will is the key to the Kingdom.
              Consequently, just like the kingdom can be further subdivided into
              the kingdom of Christ and of God, the will can also be subdivided
              into the will of the Lord and of God; this is evident in{" "}
              <b>Colossians 1:9-11</b>. There is a gradual and progressive
              journey from the knowledge of the will of the Lord into the
              knowledge of God; the will of the Lord precedes the will of God.
              Note that our Lord Jesus is both Lord and God{" "}
              <b>(John 20:28, Acts 2:36, Heb 1:8)</b>.
            </Content>
            <Content>
              Meanwhile, Paul explains to us in <b>Romans 12:2</b> that the will
              can be subdivided into three stages:{" "}
              <b>good, acceptable, and perfect</b>. Interestingly, just as we
              fitted the three life expressions (righteousness, peace, and joy)
              into the statures of Christ and God, we can also fit the three
              stages of the will into the Spirit portions of the Lord and of
              God, taking a very broad view.
            </Content>
            <Italics>
              Isa 61:1-3a — The Spirit of the Lord GOD is upon me; because the
              Lord hath anointed me to preach good tidings unto the meek; he
              hath sent me to bind up the brokenhearted, to proclaim liberty to
              the captives, and the opening of the prison to them that are
              bound; To proclaim the acceptable year of the Lord, and the day of
              vengeance of our God; to comfort all that mourn…
            </Italics>
            <Content>
              It is easy to observe that the "good tidings" until the
              "acceptable year" are under the administration of the{" "}
              <b>"Lord" (Luke 4:18-20)</b>, while the rest are under the
              administration of <b>"God"</b>. This is no coincidence. We can
              deduce that the administration of God is for perfection{" "}
              <b>(Psalm 50:2)</b>. Let's also point out that taking a broad view
              of the kingdom and the will, while righteousness and peace is good
              and acceptable <b>(1 Tim 2:1-3)</b>; Joy is perfect{" "}
              <b>(Lam 2:15)</b>.
            </Content>
          </SubSection>
          <SubSection>
            <Content>
              <Subtitle>E. Patterns of the Kingdom</Subtitle>
              Interestingly, we find allegories in scriptures that help us
              better grasp the two persons and three domains/expressions of the
              kingdom.
            </Content>
            <SubSection>
              <Content>
                <Subtitle>I. Heaven and Earth</Subtitle>
                The first and most interesting pattern of the kingdom is seen in
                the creation of the world. We can use this pattern because Jesus
                describes the kingdom of God in <b>John 3:1-13</b> as "heavenly
                things" and "earthly things". This pattern shows us the kingdom
                in the context of the two persons: Christ and God.
              </Content>
            </SubSection>
            <SubSection>
              <Content>
                <Subtitle>II. Heavens and the Angels</Subtitle>
                From scripture, we see that the kingdom is also described as the
                kingdom of heaven. Interestingly, the heavens comprise three
                heavens or places <b>(Eph 1:18)</b>, as Paul mentions the "third
                heaven" in <b>2 Corinthians 12:1-3</b>. Also, we see that we
                have three classes of Angels as in <b>Psalm 103:19-22</b>,
                namely: his angels, his ministers, and his works. These three
                divisions of the heavens and angels show us a pattern of the
                three domains or expressions of the kingdom of God:
                righteousness, peace, and Joy.
              </Content>
            </SubSection>
            <SubSection>
              <Content>
                <Subtitle>III. Tabernacle of Moses and the Priests</Subtitle>
                Interestingly, the tabernacle of Moses, which is patterned after
                the heavens <b>(Heb 8:3)</b>, was divided into three places,
                namely: court, sanctuary, and holiest of all <b>(Heb 9:1-5)</b>.
                Also, the priests were in three categories, namely: levites,
                priests, and high priests. These three divisions of the
                tabernacle and priests show us a pattern of the three domains or
                expressions of the kingdom of God: righteousness, peace, and
                Joy.
              </Content>
            </SubSection>
          </SubSection>
          {/* Kingdom Table */}

          {/* Kingdom Table */}
          <SubSection>
            <Content>
              In sum, we have provided a tabular projection of the scriptural
              exegesis of the kingdom for easier conceptualization.
            </Content>
            <Box
              sx={{ overflowX: "scroll", mt: 2, mb: 2, width: [400, "100%"] }}
            >
              <Table
                sx={{
                  width: 600,
                  borderCollapse: "collapse",
                  "& td, & th": {
                    border: "1px solid #333",
                    padding: "8px 10px",
                    fontSize: "0.875rem",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      "Kingdom Entities",
                      "Statures",
                      "Creation",
                      "Heavens",
                      "Expressions",
                      "Will",
                      "Angels",
                      "Tabernacle",
                      "Priests",
                    ].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          backgroundColor: "#f5f5f5",
                          fontWeight: "bold",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell rowSpan={3} sx={{ verticalAlign: "middle" }}>
                      Father, Jesus, Holy Ghost
                    </TableCell>
                    <TableCell rowSpan={2} sx={{ verticalAlign: "middle" }}>
                      Christ
                    </TableCell>
                    <TableCell rowSpan={2} sx={{ verticalAlign: "middle" }}>
                      Earth
                    </TableCell>
                    <TableCell>First</TableCell>
                    <TableCell>Righteousness</TableCell>
                    <TableCell>Good</TableCell>
                    <TableCell>Works</TableCell>
                    <TableCell>Court</TableCell>
                    <TableCell>Levites</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Second</TableCell>
                    <TableCell>Peace</TableCell>
                    <TableCell>Acceptable</TableCell>
                    <TableCell>Ministers</TableCell>
                    <TableCell>Sanctuary</TableCell>
                    <TableCell>Priests</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>God</TableCell>
                    <TableCell>Heaven</TableCell>
                    <TableCell>Third</TableCell>
                    <TableCell>Joy</TableCell>
                    <TableCell>Perfect</TableCell>
                    <TableCell>Angels</TableCell>
                    <TableCell>Holiest</TableCell>
                    <TableCell>High Priests</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </SubSection>
        </Section>

        {/* Section 5: Knowledge of His Will */}
        <Section>
          <Title>5. The Knowledge of His Will</Title>
          <br />
          <Picture src={knowledge} />
          <br />
          <br />
          <Italics>
            Col 1:9-10a — For this cause we also, since the day we heard it, do
            not cease to pray for you, and to desire that ye might be filled
            with the knowledge of his will in all wisdom and spiritual
            understanding; That ye might walk worthy of the Lord unto all
            pleasing…
          </Italics>
          <Content>
            It is crystal clear from the scriptures that the will of God can
            only be accessed by knowledge <b>(Eph 5:17)</b>. This is because the
            will of God is as mysterious as God himself and would therefore
            require knowledge to comprehend it{" "}
            <b>(Col 1:9, Eph 1:9, Eph 1:17, Eph 3:4)</b>. Knowledge is the
            theoretical arrangement of scriptures through the Spirit of wisdom
            and revelation under a minister of his will (like Paul){" "}
            <b>(Eph 3:1-4)</b>.
            <br />
            <br />
            Knowledge enlightens the understanding of the believer to be able to
            prove or discern the will <b>(2 Tim 3:15, Eph 1:17-18)</b>.
            Knowledge is more than just personally understanding some portions
            of scripture, but rather, being able to align all scriptures to be
            able to discern the person of our Lord Jesus Christ{" "}
            <b>(John 5:39, Luke 24:27, 2 Tim 3:7)</b>. Interestingly, Knowledge
            strengthens the believer to be able to do the will when it is
            revealed <b>(Prov 24:5, Isaiah 33:6)</b>.
          </Content>
          <SubSection>
            <Content>
              <Subtitle>I. The Realities of Knowledge</Subtitle>
              According to scriptures in <b>Eph 1:17-19</b>, which is an
              expansion of Paul's prayer for the church in Colosse{" "}
              <b>(Col 1:9)</b>, it is clearly seen that every believer who has
              come to knowledge must know by wisdom and understanding the
              following:
            </Content>
            <Bullet>What is the hope of his calling</Bullet>
            <Bullet>
              What is the riches of the glory of his inheritance in the saints
            </Bullet>
            <Bullet>
              And what is the exceeding greatness of his power to us-ward who
              believe, according to the working of his mighty power
            </Bullet>
            <Content>
              A believer who is receiving knowledge will come into a realization
              or dawning of these three things. Knowledge redefines WHAT the
              believer calls hope, riches, and power. The purpose of knowledge
              is to bridge the gap in understanding that exists between man and
              God <b>(Isaiah 55:8-9)</b>. However, it is good to establish here
              that having the knowledge of his will is different from his will
              or knowing his will.
            </Content>
          </SubSection>
          <SubSection>
            <Content>
              <Subtitle>II. Understanding Doctrine</Subtitle>
              As we have established earlier, the will of God are laws that
              govern God's life. Knowledge or knowledge of his will only
              configures the soul to be able to discern the will, which are the
              laws. <b>Isaiah 28:9</b> clearly distinguishes Knowledge from
              doctrine, showing that knowledge precedes doctrine. It is good to
              know that doctrine are laws, commandments, or instructions{" "}
              <b>(Prov 4:2, Matt 15:9)</b>.
              <br />
              <br />A good way to understand the disparity that exists between
              knowledge and doctrine is that while Knowledge teaches us{" "}
              <b>"the what" (Eph 1:18-19)</b>, doctrine makes us understand{" "}
              <b>"the how" (Eph 3:16-19)</b>. Doctrines are commandments; a
              realization of what to do <b>(Mark 7:7)</b>. We can conclude that
              the will of God is doctrine and not knowledge. Hence, the
              knowledge of his will prepares the soul to understand the will of
              God.
            </Content>
          </SubSection>
          <SubSection>
            <Content>
              <Subtitle>III. Whom shall he teach knowledge?</Subtitle>
              It is interesting to note that knowledge and doctrine can only be
              given to a person who has been weaned from Milk and drawn from the
              breast <b>(Isaiah 28:9)</b>. In the light of Paul's prayer for the
              churches in Ephesus and Colosse; it was after he heard of their
              faith in the Lord Jesus and love unto all the saints, that he
              began praying for them to come into knowledge{" "}
              <b>(Eph 1:15, Col 1:4)</b>. We can thus conclude that these
              attributes are fruits of the milk, according to <b>Isaiah 28:9</b>
              ; which gives the sense that anyone who would come into knowledge
              ought to undergo the tutelage/learning of the milk.
            </Content>
          </SubSection>
        </Section>

        {/* Section 6: Milk */}
        <Section>
          <Title>6. Milk: The Preparation for the Knowledge of His Will</Title>
          <br />
          <Picture src={milk} />
          <br />
          <br />
          <Italics>
            Eph 1:15-17 — Wherefore I also, after I heard of your faith in the
            Lord Jesus, and love unto all the saints, Cease not to give thanks
            for you, making mention of you in my prayers; That the God of our
            Lord Jesus Christ, the Father of glory, may give unto you the spirit
            of wisdom and revelation in the knowledge of him.
          </Italics>
          <Content>
            As earlier established, the will of God can only be accessed through
            knowledge. However, before anyone can be taught or receive
            knowledge, such a person must first be properly grounded in the
            "milk" <b>(Isaiah 28:9)</b>. This foundational allocation of God's
            Word is commonly referred to in Scripture as milk{" "}
            <b>(Hebrews 5:12–14; 1 Corinthians 3:1–2; Isaiah 28:9)</b>.
          </Content>
          <Content>
            Without a proper engagement with the milk, a soul cannot receive or
            sustain knowledge, because the milk is what prepares and conditions
            the soul for it. It lays the necessary foundation upon which
            knowledge can be built. Just as a child cannot handle solid food
            without first being nourished by milk, a believer cannot handle the
            weight of spiritual knowledge or meat without first being trained in
            the milk <b>(1 Cor 3:1-3, John 4:34)</b>.
          </Content>
          <SubSection>
            <Content>
              <Subtitle>
                A. What is Milk? — First Principles of the Oracles of God /
                Apostles' Doctrine (Hebrews 5:12–14)
              </Subtitle>
              Milk refers to the first principles of the oracles of God, also
              known as the Apostles' doctrine. These are the foundational
              teachings of God's Word given to establish a believer at the
              beginning of their walk with God{" "}
              <b>(Hebrews 5:12–14; 6:1, Acts 2:42)</b>. Scripture outlines six
              (6) principles as the basis of this milk:
            </Content>
            <SubSection>
              <Content>
                <Subtitle>I. Repentance From Dead Works</Subtitle>
                To repent means to turn from one's way and to embrace what is
                right in God's eyes. The word repentance in the New Testament is
                derived from the Greek word <b>"metanoia"</b>, which literally
                means to have a change of heart or a change in thinking, which
                very often results in corresponding action, rather than mere
                emotional regret. Repentance from dead works is the forsaking of
                habits, thoughts, philosophies, and ways that are contrary to
                God's standard, which is revealed when the light of God's word
                shines <b>(2 Corinthians 7:9-10, Acts 8:22)</b>.
                <br />
                <br />
                Repentance from dead works begins with acknowledging our
                shortcomings, when God's standard is shown to us, and is not
                complete without corrective action taken in light of what God
                shows as right and just <b>(Acts 2:38, Acts 3:19)</b>. The
                teaching of repentance from dead works is a foundational
                principle of our faith and is a necessary ingredient for healthy
                Christian living. It teaches us not to be comfortable with sins,
                and as such, to desire to be continually cleansed from them by
                feeding and obedience.
              </Content>
            </SubSection>
            <SubSection>
              <Content>
                <Subtitle>II. Faith Towards God</Subtitle>
                Faith is a spiritual substance formed in the heart when
                understanding of God's word enters the heart, resulting in a
                strong conviction in God's person and persuasion concerning His
                promises, and His faithfulness to fulfil them. Faith, which both
                comes and grows by feeding and meditating on God's word, teaches
                the believer to lean more on the invisible (word) by helping us
                to understand that the invisible (word of God) controls the
                visible (our earthly affairs) <b>(Hebrews 11:1-3)</b>.
                <br />
                <br />
                Faith towards God is the reliance and assurance in God that is
                progressively built in the believer as a result of
                experientially feeding on and obeying God's word, even in the
                face of contradicting and trying situations. It teaches a
                believer to identify and trust God for whatever his word has
                said concerning their desires and needs <b>(Mark 11:24)</b>.
                Much more, this doctrine teaches believers to trust God with
                their lives and God's word instead of their own intelligence or
                strength and wait patiently for him <b>(Proverbs 3:5-6)</b>.
              </Content>
            </SubSection>
            <SubSection>
              <Content>
                <Subtitle>III. Doctrine of Baptisms</Subtitle>
                The word Baptism literally means to Immerse, to "deep into" or
                to "go deeper"; the Christian journey is all about going deeper,
                or being immersed continuously in the things of God. The
                doctrine of baptism teaches a saint not to be satisfied with
                one's current estate in his/her journey with God no matter the
                milestone or achievements attained <b>(Jeremiah 48:11a)</b>.
                <br />
                <br />
                In the Christian Journey, there are different types of baptism,
                such as baptism in the body of Christ{" "}
                <b>(1 Corinthians 12:13)</b>, Baptism in the Holy Ghost{" "}
                <b>(Acts 1:5)</b>, Baptism by water <b>(Matthew 3:16)</b> and
                Baptism by teaching/Preaching <b>(Matthew 28:19-20)</b>.
                However, it is good to further emphasize that the doctrine of
                baptism as in the early church raises souls that are deeply
                rooted together in fellowship with other Saints; when saints
                fellowship with one another, they are being baptised much deeper
                into one spirit, which births a lifestyle of Faith and Hope{" "}
                <b>(1 Corinthians 10:2, 1 Corinthians 12:12-13)</b>. Baptism
                also teaches us to submit ourselves under the mighty hand of God{" "}
                <b>(1 Pet. 5:6)</b>.
              </Content>
            </SubSection>
            <SubSection>
              <Content>
                <Subtitle>IV. Laying On of Hands</Subtitle>
                The doctrine of laying on of hands physically implies hands
                being laid on an individual typically for impartation,
                ordination, healing or transference of grace or anointing{" "}
                <b>(Acts 8:18, 1 Tim 4:14)</b>; whilst this is very scriptural
                and accepted, laying on of hands, much more teaches a soul to be
                subject to higher authorities <b>(Romans 13:1)</b> because the
                less is blessed of the better <b>(Hebrews 7:7)</b>.
                <br />
                <br />
                Even when the physical hands are not being laid, a believer
                should always have a heart posture of coming under authority in
                teaching, instruction, guidance and obedience{" "}
                <b>(Hebrews 13:17)</b>. Jesus our Lord exemplified this heart
                posture <b>(Matthew 3:14-15)</b>. A soul that does not honor men
                firstly has broken the commandments of the scriptures, is
                exposed to the devil, and cannot go far with God{" "}
                <b>(1 Timothy 5:17, 1 Peter 5:5)</b>. This doctrine also teaches
                us to trust in the work God has raised in men to guide us{" "}
                <b>(1 Corinthians 11:1)</b>.
              </Content>
            </SubSection>
            <SubSection>
              <Content>
                <Subtitle>V. Resurrection of the Dead</Subtitle>
                Resurrection of the dead is the transformation of the bodies of
                all believers into glorious bodies. This change will occur
                during the Second Coming of Jesus Christ for the Church on earth{" "}
                <b>(Acts 1:11; 1 Thessalonians 4:13–18)</b>. This is the final
                redemption experience of the bodies of all believers{" "}
                <b>(Philippians 3:21; 1 Corinthians 15:49–53)</b>. However, for
                unbelievers, it will be a resurrection unto eternal damnation{" "}
                <b>(Daniel 12:2)</b>. This principle teaches that there is a
                hope of change in the Christian faith. Our lives here on earth
                are temporal, so we should live intentionally with the hope of
                our final dwelling place <b>(2 Corinthians 4:18)</b>.
                <br />
                <br />
                Resurrection of the dead encourages us to give freely and not be
                afraid of a future that may seem uncertain. As believers, we
                have been raised in our spirits at the new birth, we are being
                raised in our souls, and we will ultimately be raised in our
                bodies. Resurrection of the dead is something to long for and
                should create joy in our hearts <b>(1 Thess. 4:18)</b>.
              </Content>
            </SubSection>
            <SubSection>
              <Content>
                <Subtitle>VI. Eternal Judgement</Subtitle>
                Eternal judgment is the teaching that God will judge every
                person who has lived on earth after they leave this life. This
                teaching instructs believers not to take their Christian walk
                lightly, but to be conscious of their actions every day{" "}
                <b>(1 Corinthians 4:5)</b>. It is a judgment that cannot be
                overturned. It is meant to cultivate a reverent fear of God.
                God, who is love, is also just and severe. A day has been
                appointed by the Lord for every person to give an account of the
                things done on earth. It causes the believer to remain conscious
                of God even when alone — not to live according to the flesh, but
                according to the Spirit <b>(Romans 8:1, 13)</b>.
              </Content>
            </SubSection>
          </SubSection>
        </Section>

        {/* Section 7: Faith and Love */}
        <Section>
          <Title>7. Faith in the Lord Jesus and Love unto All the Saints</Title>
          <br />
          <Picture src={faith} />
          <br />
          <br />
          <Content>
            The essence of the first principles of the oracles of God is that
            faith and love should be worked in the believer. Faith and Love are
            the fruits of the milk in the life of the believer.
          </Content>
          <SubSection>
            <Content>
              <Subtitle>A. Faith in the Lord Jesus</Subtitle>
              This is an unconditional trust and dependence in the person and
              words of Jesus <b>(Luke 5:5, Matt. 8:8)</b>. It makes a soul come
              to a place where they love the Lord Jesus more than the things
              (vanities) that they desire from Him. Faith in the Lord Jesus
              begins with trusting Him to meet all your needs. It is trusting
              the ability of the Lord more than your own abilities and
              limitations. In essence, it is to trust Jesus more than yourself.
              Even when those things you trust Him for do not come, you still
              love Him more than them. Eventually, that soul graduates to being
              able to suffer the loss of things, friendships, reputation, and
              even the willingness to suffer the loss of their own life. Jesus
              means more to that believer than the things he calls "life". A
              lifestyle is formed in the believer that makes his conviction in
              the person of Jesus more real than any external
              circumstance/situation, hence, an unwavering faith in the Lord.
            </Content>
          </SubSection>
          <SubSection>
            <Content>
              <Subtitle>B. Love to All the Saints</Subtitle>
              This refers to a love walk towards fellow believers, cultivated in
              the soul by the milk. At its peak, it is demonstrated in loving
              believers more than what you call life. A soul with this fruit
              loves not because of the reward of doing so, or the consequence of
              not doing so, but because he sees every saint as Jesus. Love for
              all the saints begins with discerning all believers and
              identifying with them as members of the same body — the body of
              Christ <b>(Acts 6:1-3, Rom. 12:5)</b>. Such a soul will not seek
              to tear down the church or ministries/communities within the body.
              They understand with certainty that love is learnt in community.
              No saint stands alone and aloof.
            </Content>
          </SubSection>
          <SubSection>
            <Content>
              <Subtitle>C. Fear of the Lord</Subtitle>
              Fear, here, is not a morbid one like the way one might fear a
              snake. Rather, it describes a sense of awe, honor, and reverence
              for the Lord and his servants <b>(Acts 2:43, 5:5)</b>. It is also
              being conscious of the truth that we all shall stand before the
              Lord someday <b>(Eccl 12:13-14)</b>. This fear empowers the
              believer to keep or obey God's commandments no matter the cost of
              it.
            </Content>
          </SubSection>
        </Section>

        {/* Section 8: Weaning */}
        <Section>
          <Title>
            8. Transitioning From Milk to the Knowledge of His Will: Weaning
            Makes Poor and Meek
          </Title>
          <br />
          <Picture src={transitioning} />
          <br />
          <br />
          <Italics>
            Isaiah 28:9 — Whom shall he teach knowledge? and whom shall he make
            to understand doctrine? them that are weaned from the milk, and
            drawn from the breasts.
          </Italics>
          <Italics>
            Isa 61:1 — The Spirit of the Lord GOD is upon me; because the LORD
            hath anointed me to preach good tidings unto the meek; he hath sent
            me to bind up the brokenhearted, to proclaim liberty to the
            captives, and the opening of the prison to them that are bound.
          </Italics>
          <Content>
            After Faith, love, and fear have been produced in a soul, there's
            also a weaning process from the milk that needs to occur in a soul.
            The experience of weaning from the milk is what makes a soul poor
            and meek to be able to be taught knowledge because the gospel is to
            the poor and meek <b>(Luke 4:18, Isaiah 61:1)</b>.
            <br />
            <br />
            To be poor is to be able to summarize all your riches/everything you
            have known before of God as insufficient. This is a cry for more of
            God. A poor soul is one who can agree that all that they have known
            of God is not all that there is to God <b>(Matt 5:2)</b>. Poverty
            can be worked by the Lord's dealing on a particular soul through the
            person's upbringing, circumstances of life, etc. Moreover, it is not
            enough to be poor; the soul must also be meek. You can cry for more
            of God, but you need meekness to receive knowledge. Meekness makes a
            soul teachable <b>(James 1:21, Psalm 25:9, Acts 8:27-31)</b>.
          </Content>
        </Section>

        {/* Section 9: A Minister of His Will */}
        <Section>
          <Title>9. A Minister of His Will: Paul as an Example</Title>
          <br />
          <Picture src={minister} />
          <br />
          <br />
          <Italics>
            Eph 1:1 — Paul, an apostle of Jesus Christ by the will of God, to
            the saints which are at Ephesus, and to the faithful in Christ
            Jesus.
          </Italics>
          <Content>
            From today's teaching, we have learnt that the Knowledge of the Will
            of God cannot be accessed at plain sight from the scriptures. They
            are secrets and mysteries that can be harvested by a minister whom
            God has raised for that purpose. Because of the dealings of God and
            the heightened operation of the Spirit of Wisdom and Revelation upon
            that minister, he can shed light and chart direction from scriptures
            to give the knowledge of the will <b>(Gal 2:2)</b>.
          </Content>
          <Content>
            This minister is raised for a company of people in different
            generations so that the Will can be accessed by knowledge,
            understood, and the doctrine of that will ultimately be obeyed.
            This, in turn, is what transforms such souls into the image of a
            person.
            <br />
            <br />
            We can see this pattern of ministers in the Scriptures{" "}
            <b>(Rom 10:13-15)</b>. Paul the Apostle is a perfect example. We can
            see from the scripture above that the Apostle Paul was raised by
            Jesus, a minister of the will of God. This means that Paul's
            apostleship was because of the will and unto this will{" "}
            <b>(2 Tim 1:1, 2Cor 1:1)</b>. He was commissioned by Jesus to point
            the church in the direction of the Kingdom <b>(Acts 26:16-18)</b>.
            By this calling and ordination, Paul was able to have access to the
            will and to do it. This ultimately opened the early church to seeing
            the kingdom and entering it. For the sake of this will, Paul went
            through dealings and sufferings <b>(Acts 9:16)</b>. Through Apostle
            Paul, the knowledge of his will was given to the church{" "}
            <b>(Col 1:9)</b>. This was the purpose of the many letters of Paul
            to the churches. It was necessary for Paul to preach and teach so
            that many could enter to see and do the Will of God{" "}
            <b>(1 Cor 9:16-17)</b>. If this weren't taught and opened up, the
            church would remain unweaned from the milk and would have no access
            to the knowledge and doctrine of the will.
          </Content>
        </Section>
      </Section>

      {/* Conclusion */}
      <Section>
        <SubSection>
          <Title>10. Conclusion</Title>
          <Content>
            We have been able to learn the fundamentals of the will: the
            definition and purpose of the will, and who can be favoured enough
            to receive the knowledge of the will. In our next study, we will be
            going deeper into what exactly the content of the will is. See you
            tomorrow!
          </Content>
        </SubSection>
      </Section>

      <WithLove />
    </>
  );
}
